/**
 * plan-rehydrate — pure helpers deriving Smart Recommendations R1's
 * per-thread "explored Need" state from a reloaded thread's persisted
 * messages (frontend §7 rehydration task).
 *
 * "Explored" is derived by EXACT `need_id`: the backend persists which Need a
 * fetch turn resolved on `chat_messages.need_id` (`garden/chat/models.py`) and
 * exposes it on the `ThreadMessage` wire (`garden/chat/schemas/threads.py`,
 * `GET /chat/threads/{id}`). A chip tap OR a §3.2b typed resolution both write
 * that column, so reload marks the exact Need done — no label-matching
 * heuristic.
 */
import type { Need } from "@/features/chat/useChatStream";

/** Mirrors `garden.chat.orchestrator._FETCH_NEED_INTENT` — the sentinel
 * `intent` recorded on a Need-fetch assistant turn (a chip tap OR a typed
 * turn resolved against the plan), never a generative call. */
export const FETCH_NEED_INTENT = "smart-rec-fetch";

/** The subset of `ThreadMessage` `collectPlanNeeds` reads. */
export interface PlanJsonMessage {
  plan_json?: Need[] | null;
}

/** The subset of `ThreadMessage` `deriveExploredNeedIds` reads — kept
 * minimal so this stays a pure, component-free, easily unit-tested module. */
export interface RehydrateMessage {
  role: "user" | "assistant" | "system";
  content: string;
  intent: string | null;
  /** The Need this turn resolved (exact), from the ThreadMessage wire. NULL on
   * every turn that resolved no need. */
  need_id?: string | null;
}

/**
 * Every plan Need ever surfaced in this thread, keyed by `need_id` (a later
 * message wins on a duplicate slug — in practice `need_id` is a
 * content-derived slug, so this is stable). Reads every message's
 * `plan_json` (when present); order is insertion order (oldest turn first).
 */
export function collectPlanNeeds(
  messages: readonly PlanJsonMessage[],
): Need[] {
  const byId = new Map<string, Need>();
  for (const m of messages) {
    for (const need of m.plan_json ?? []) {
      byId.set(need.need_id, need);
    }
  }
  return [...byId.values()];
}

/**
 * The `need_id`s already fetched anywhere in this thread (spec §3.2b
 * "explored" = a Need already fetched), by EXACT match: any assistant turn
 * whose persisted `need_id` is one of the plan's known needs. `needs` is every
 * Need ever surfaced in the thread (`collectPlanNeeds`) — used only to keep
 * the result scoped to known needs (defensive; a fetched need_id is always a
 * plan need).
 */
export function deriveExploredNeedIds(
  messages: readonly RehydrateMessage[],
  needs: readonly Need[],
): Set<string> {
  const known = new Set(needs.map((n) => n.need_id));
  const explored = new Set<string>();
  for (const m of messages) {
    if (m.role !== "assistant") continue;
    const nid = m.need_id ?? null;
    if (nid && known.has(nid)) {
      explored.add(nid);
    }
  }
  return explored;
}
